import { ErrorModal, getMomentTz, HelmetDexteel } from "@dexteel/mesf-core";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { StatusIndicator } from "@/shared/components/StatusIndicator";
import { ATHENS_GRAY } from "@/shared/colors";
import { CleaningCycleCard } from "./components/CleaningCycleCard";
import { EquipmentStatusPanel } from "./components/EquipmentStatusPanel";
import { LastBarsCard } from "./components/LastBarsCard";
import { ProductSummaryCard } from "./components/ProductSummaryCard";
import { ShiftGantt } from "./components/ShiftGantt";
import {
	BreakBannerRow,
	BreakCountdownPill,
} from "./components/BreakCountdown";
import { ShiftHeaderPill } from "./components/ShiftHeaderPill";
import { ShiftKPISection } from "./components/ShiftKPISection";
import { ShiftMetricsCard } from "./components/ShiftMetricsCard";
import { useOverviewData } from "./hooks/useOverviewData";
import { OverviewLiveSignals } from "./models/types";

const moment = getMomentTz();

const LOW_TRIGGERED_ESTOPS: (keyof OverviewLiveSignals)[] = [
	"DestackerE",
	"DestackerW",
	"Stacker",
	"StationE",
	"StationW",
	"EastGate1",
	"EastGate2",
	"EastGate3",
	"EastGate4",
	"WestGate1",
	"WestGate2",
	"WestGate3",
	"WestGate4",
	"SystemSafetyEstop",
];

const liveSignalsIndicateDelay = (live: OverviewLiveSignals): boolean => {
	if (LOW_TRIGGERED_ESTOPS.some((k) => live[k] === 0)) return true;
	if (live.HydEstop === 1 || live.SystemEstop === 1) return true;
	if (
		live.R1Alarm === 1 ||
		live.R2Alarm === 1 ||
		live.R3Alarm === 1 ||
		live.R4Alarm === 1 ||
		live.R5Alarm === 1 ||
		live.R6Alarm === 1
	)
		return true;
	if (live.HydHiTempAlarm === 1 || live.HydLoTempAlarm === 1) return true;
	return false;
};

// Canonical "is the line in delay?" — DEL.Delays open row is source of truth,
// OR'd with live signals to stay responsive in the gap before TrackDelaysJob
// picks up a new event.
const isLineInDelay = (live: OverviewLiveSignals | null): boolean => {
	if (!live) return false;
	if (live.IsInDelay === 1) return true;
	return liveSignalsIndicateDelay(live);
};

export const WeldlineOverviewPage = () => {
	const params = useParams<{ assetName?: string }>();
	const [error, setError] = useState("");
	const handleError = useCallback((e: Error) => setError(e.message), []);

	const { data, isLoading } = useOverviewData({
		assetId: null,
		onError: handleError,
	});

	const loading = isLoading && !data;
	const assetName = data?.header?.AssetName ?? params.assetName ?? "Weldline";
	const inDelay = isLineInDelay(data?.live ?? null);
	// Open delay only: alarms can stay on while the line welds.
	const isRunning = data?.live?.IsInDelay !== 1;

	// Gantt window = active shift's full range, same boundary as the Current
	// Shift metrics card.
	const ganttStart = data?.header?.CurrShiftStart ?? null;
	const ganttEnd = data?.header?.CurrShiftEnd ?? null;

	const today = new Date();

	return (
		<>
			<HelmetDexteel title={`${assetName} Overview`} />

			<Box sx={{ p: 1.5, backgroundColor: ATHENS_GRAY, minHeight: "100%" }}>
				<Grid2 container spacing={1}>
					<Grid2 size={{ xs: 12 }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								flexWrap: "wrap",
							}}
						>
							<Typography variant="h5" sx={{ fontWeight: 600 }}>
								Weldline {assetName.replace(/^Weld/i, "") || ""} Overview
							</Typography>
							<StatusIndicator isInDelay={inDelay} />
							{data?.header?.ServerTime && (
								<Typography
									variant="caption"
									sx={{ color: "#666", fontStyle: "italic" }}
									title={moment(data.header.ServerTime).format(
										"YYYY-MM-DD HH:mm:ss",
									)}
								>
									Last updated at{" "}
									{moment(data.header.ServerTime).format("HH:mm:ss")}
								</Typography>
							)}

							<Box sx={{ flexGrow: 1 }} />

							<BreakCountdownPill
								breaks={data?.breaks}
								serverTime={data?.header?.ServerTime}
								isRunning={isRunning}
							/>
							<ShiftHeaderPill
								today={today}
								header={data?.header}
								loading={loading}
							/>
						</Box>
					</Grid2>

					<BreakBannerRow
						breaks={data?.breaks}
						serverTime={data?.header?.ServerTime}
						isRunning={isRunning}
					/>

					<Grid2 size={{ xs: 12 }}>
						<Paper elevation={1} sx={{ p: 1, pb: 0.75 }}>
							<ShiftGantt
								shiftStart={ganttStart}
								shiftEnd={ganttEnd}
								onError={handleError}
								loading={loading}
							/>
						</Paper>
					</Grid2>

					{/* Top operational cards align with the three shift/run columns below. */}
					<Grid2 container spacing={1} size={{ xs: 12 }} alignItems="stretch">
						<Grid2 size={{ xs: 12, lg: 5 }}>
							<Paper
								elevation={1}
								sx={{
									p: 1.5,
									border: "2px solid #4CAF50",
									borderRadius: 2,
									height: "100%",
								}}
							>
								<ProductSummaryCard
									live={data?.live ?? null}
									productRuns={data?.productRuns ?? []}
									loading={loading}
								/>
							</Paper>
						</Grid2>

						<Grid2 size={{ xs: 12, lg: 3 }}>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 1,
									height: "100%",
								}}
							>
								<Paper elevation={1} sx={{ p: 1 }}>
									<LastBarsCard bars={data?.lastBars ?? []} loading={loading} />
								</Paper>
								<Paper elevation={1} sx={{ px: 1, py: 0.75 }}>
									<CleaningCycleCard
										cleaning={data?.cleaning ?? []}
										loading={loading}
									/>
								</Paper>
							</Box>
						</Grid2>

						<Grid2 size={{ xs: 12, lg: 4 }}>
							<Paper
								elevation={1}
								sx={{ p: 1, borderRadius: 2, height: "100%" }}
							>
								<Typography
									variant="subtitle2"
									sx={{ fontWeight: 600, mb: 0.5 }}
								>
									Live signals
								</Typography>
								<EquipmentStatusPanel
									live={data?.live ?? null}
									loading={loading}
								/>
							</Paper>
						</Grid2>
					</Grid2>

					<Grid2 container spacing={1} size={{ xs: 12 }}>
						<Grid2 size={{ xs: 12, lg: 4 }}>
							<Paper elevation={1} sx={{ p: 1, height: "100%" }}>
								<ShiftMetricsCard
									title="Current shift"
									subtitle={
										data?.header
											? `Shift ${data.header.CurrShiftLabel ?? "-"} · Crew ${data.header.CurrCrew ?? "-"}`
											: undefined
									}
									metrics={data?.currentShift ?? null}
									accent="current"
									loading={loading}
									serverTime={data?.header?.ServerTime ?? null}
								/>
							</Paper>
						</Grid2>
						<Grid2 size={{ xs: 12, lg: 4 }}>
							<Paper elevation={1} sx={{ p: 1, height: "100%" }}>
								<ShiftMetricsCard
									title="Previous shift"
									subtitle={
										data?.header
											? `Shift ${data.header.PrevShiftLabel ?? "-"} · Crew ${data.header.PrevCrew ?? "-"}`
											: undefined
									}
									metrics={data?.previousShift ?? null}
									accent="previous"
									loading={loading}
								/>
							</Paper>
						</Grid2>
						<Grid2 size={{ xs: 12, lg: 4 }}>
							<Paper elevation={1} sx={{ p: 1, height: "100%" }}>
								<ShiftMetricsCard
									title="Current Production Run"
									subtitle={
										data?.currentRun?.PartNumber
											? `Product ${data.currentRun.PartNumber}`
											: "Latest tracked run"
									}
									metrics={data?.currentRun ?? null}
									accent="run"
									loading={loading}
								/>
							</Paper>
						</Grid2>
					</Grid2>

					<Grid2
						container
						spacing={1}
						size={{ xs: 12 }}
						sx={{ alignItems: "stretch" }}
					>
						<Grid2 size={{ xs: 12, xl: 6 }}>
							<Paper elevation={1} sx={{ p: 1, height: "100%" }}>
								<ShiftKPISection
									title="Current Shift"
									metrics={data?.currentShift ?? null}
									loading={loading}
								/>
							</Paper>
						</Grid2>
						<Grid2 size={{ xs: 12, xl: 6 }}>
							<Paper elevation={1} sx={{ p: 1, height: "100%" }}>
								<ShiftKPISection
									title="Previous Shift"
									metrics={data?.previousShift ?? null}
									loading={loading}
								/>
							</Paper>
						</Grid2>
					</Grid2>
				</Grid2>
			</Box>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
